import {
  CustomerPartitionCheckResultSchema,
  CustomerPartitionUsageSchema,
  IndustryBrandCountsSchema,
  MAX_ACCESS_BY_TENANT_BATCH,
  MAX_USERS_ACCESS_BATCH,
  OnboardingWritesAvailabilitySchema,
  TenantOnboardingReadModelSchema,
  type ClientsParamsSetupInput,
  type CustomerPartitionCheckResult,
  type CustomerPartitionUsage,
  type IndustryBrandCounts,
  type NewOrganizationOnboardingInput,
  type OnboardingWritesAvailability,
  type TenantOnboardingReadModel,
  type PreflightMarketCatalogueEntry,
} from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import { PollBudgetElapsedError } from "@/lib/poll-budget-elapsed";
import { fetchAmberOptions } from "@/lib/queries/amber-options";
import type { BrandOption } from "@/features/opportunities/api/opportunity-sets.api";
import type { AddPreflightMarketInput, CurrencyCode, EnabledOpportunityTypes, IndustryDefaultBrandUpdate, MarketplaceOrderHandoffDefaults, NorthStarKpi } from "@nexus/contracts";

// ─── Read Endpoints ─────────────────────────────────────────────────

export interface UserItem {
  id: string;
  displayName: string;
  email: string;
  status: string;
  accessIds: string[];
  lastSeenAt: string | null;
  createdAt: string;
  authProvider: string | null;
  photoUrl: string | null;
  // Backend no longer surfaces the bearer `token` here — that secret is
  // returned only at create/regenerate time, never on subsequent reads.
  // See services/api/src/routes/users.ts (GET /users/all). UI surfaces that
  // need the share link must trigger /invites/regenerate to obtain a fresh
  // token.
  pendingInvite: { inviteId: string; expiresAt: string; accessId: string; hasInvite: true } | null;
}

export interface PaginatedUsers {
  items: UserItem[];
  nextCursor?: string;
  hasMore: boolean;
  totalCount?: number;
}

export type IamUserSortBy = "name" | "email" | "status" | "createdAt" | "role" | "orgs";

export interface IamUsersListFilters {
  search?: string;
  tenantId?: string;
  accessName?: string;
  /** Restrict to members of a specific role (access group). Used by the role drawer. */
  accessId?: string;
  status?: string;
  noRole?: boolean;
  hasPendingInvite?: boolean;
  sortBy?: IamUserSortBy;
  sortOrder?: "asc" | "desc";
  limit?: number;
}

export async function fetchIamUsersPage(
  filters: IamUsersListFilters,
  cursor?: string,
): Promise<PaginatedUsers> {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  if (cursor) params.set("cursor", cursor);
  if (filters.search) params.set("search", filters.search);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.accessName) params.set("accessName", filters.accessName);
  if (filters.accessId) params.set("accessId", filters.accessId);
  if (filters.status) params.set("status", filters.status);
  if (filters.noRole) params.set("noRole", "true");
  if (filters.hasPendingInvite) params.set("hasPendingInvite", "true");
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  const { data } = await authFetch<PaginatedUsers>(`/users/all?${params}`);
  return data;
}

/** @deprecated Use fetchIamUsersPage via iamUsersInfiniteQueryOptions */
export async function fetchAllUsers(limit = 50, cursor?: string): Promise<PaginatedUsers> {
  return fetchIamUsersPage({ limit }, cursor);
}

export type TemplateScopeMode = "FIXED" | "WILDCARD";

export interface CatalogTemplateRef {
  id: string;
  scopeMode: TemplateScopeMode;
  status?: "ACTIVE" | "ARCHIVED";
}

export interface UserAccessEntry {
  id: string;
  userId: string;
  accessId: string;
  access: {
    id: string;
    name: string;
    description?: string | null;
    tenantId: string;
    templateId?: string | null;
    template?: CatalogTemplateRef | null;
    tenant: { id: string; name: string; type: string; logoUrl?: string | null };
    permissions: Array<{ type: string; method: string; route: string }>;
  };
}

export async function fetchUserAccess(userId: string): Promise<UserAccessEntry[]> {
  const { data } = await authFetch<UserAccessEntry[]>(
    `/users/${encodeURIComponent(userId)}/access`,
  );
  return data;
}

/** Access trees keyed by userId, as returned by GET /users/access/batch. */
export type UsersAccessMap = Record<string, UserAccessEntry[]>;

/**
 * Fetch access trees for many users at once, collapsing the People tab's
 * former per-user N+1 into chunked requests. Returns a map keyed by userId;
 * every requested id is present, with an empty array for users that have no
 * visible access. Partial chunk failures merge successful chunks; fails only
 * when every chunk rejects.
 */
export async function fetchUsersAccess(userIds: string[]): Promise<UsersAccessMap> {
  if (userIds.length === 0) return {};

  const batches = chunk(userIds, MAX_USERS_ACCESS_BATCH);
  const results = await Promise.allSettled(
    batches.map(async (ids) => {
      const params = new URLSearchParams({ userIds: ids.join(",") });
      const { data } = await authFetch<UsersAccessMap>(`/users/access/batch?${params}`);
      return data;
    }),
  );

  const merged: UsersAccessMap = {};
  let fulfilled = 0;
  let firstRejection: unknown;
  for (const result of results) {
    if (result.status === "fulfilled") {
      fulfilled++;
      Object.assign(merged, result.value);
    } else if (firstRejection === undefined) {
      firstRejection = result.reason;
    }
  }
  if (fulfilled === 0) {
    throw firstRejection ?? new Error("Failed to load user access batch");
  }
  return merged;
}

export async function fetchUser(userId: string): Promise<UserItem> {
  const { data } = await authFetch<UserItem>(`/users/${encodeURIComponent(userId)}`);
  return data;
}

export type IamTenantSortBy = "name" | "createdAt" | "memberCount";

export interface IamTenantsListFilters {
  search?: string;
  tenantType?: "SUPER_TENANT" | "API" | "ORGANIZATION";
  size?: "large" | "medium" | "small";
  includeArchived?: boolean;
  sortBy?: IamTenantSortBy;
  sortOrder?: "asc" | "desc";
  limit?: number;
}

export interface TenantListItem {
      id: string;
      name: string;
      type: string;
      productLine?: string;
      retoolUserId?: string | null;
      description?: string | null;
      brandColor?: string | null;
      logoUrl?: string | null;
      createdAt: string;
      deletedAt: string | null;
      industryIndices: string[];
      defaultIndustry: string | null;
      defaultBrand?: string | null;
      hasProducts?: boolean;
  memberCount?: number;
}

export interface PaginatedTenants {
  items: TenantListItem[];
    nextCursor?: string;
    hasMore: boolean;
  totalCount?: number;
}

export async function fetchIamTenantsPage(
  filters: IamTenantsListFilters,
  cursor?: string,
): Promise<PaginatedTenants> {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  if (cursor) params.set("cursor", cursor);
  if (filters.includeArchived) params.set("includeArchived", "true");
  if (filters.search) params.set("search", filters.search);
  if (filters.tenantType) params.set("tenantType", filters.tenantType);
  if (filters.size) params.set("size", filters.size);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  const { data } = await authFetch<PaginatedTenants>(`/tenants?${params}`);
  return data;
}

export async function fetchAllTenants(opts: { limit?: number; cursor?: string; includeArchived?: boolean } = {}) {
  return fetchIamTenantsPage({
    limit: opts.limit,
    includeArchived: opts.includeArchived,
  }, opts.cursor);
}

export async function fetchTenant(tenantId: string): Promise<TenantListItem> {
  const { data } = await authFetch<TenantListItem>(`/tenants/${encodeURIComponent(tenantId)}`);
  return data;
}

export interface IndustryItem {
  index: string;
  title: string;
  assetCount90d: number | null;
  dataUpdated: string | null;
}

export async function fetchAllIndustries(): Promise<IndustryItem[]> {
  const { data } = await authFetch<IndustryItem[]>("/industries/all");
  return data;
}

/** Roster brand count for every industry in one request, for the Org drawer's industries table. */
export async function fetchIndustryBrandCounts(signal?: AbortSignal): Promise<IndustryBrandCounts> {
  const data = await fetchAmberOptions<unknown>("/industries/brand-counts", "/industries", { signal });
  return IndustryBrandCountsSchema.parse(data);
}

/** Brand roster of one industry, for the Org drawer's per-industry default brand picker. */
export async function fetchIndustryBrandOptions(
  industry: string,
  signal?: AbortSignal,
): Promise<BrandOption[]> {
  return fetchAmberOptions<BrandOption[]>(
    `/industries/competitor-options?${new URLSearchParams({ industry }).toString()}`,
    "/industries",
    { signal },
  );
}

export async function fetchAllMarketCatalogue(): Promise<PreflightMarketCatalogueEntry[]> {
  const { data } = await authFetch<PreflightMarketCatalogueEntry[]>("/market-catalogue/all");
  return data;
}

export interface AccessGroupItem {
  id: string;
  name: string;
  description?: string | null;
  type: "SYSTEM" | "CUSTOM";
  tenantId: string;
  templateId?: string | null;
  template?: CatalogTemplateRef | null;
  permissions: Array<{ type: string; method: string; route: string }>;
  updatedAt: string;
  createdAt: string;
  /** Server aggregate — matches IAM list member_count when present. */
  memberCount?: number;
  /** Unused, unexpired invites for this role. Cold-remove stays blocked while this is above zero. */
  pendingInviteCount?: number;
  /** Soft-delete marker — when set, the role is archived/deleted but recoverable. */
  deletedAt?: string | null;
}

export interface AccessGroupDetail {
  id: string;
  name: string;
  tenantId: string;
  permissions: Array<{ type: string; method: string; route: string }>;
}

export async function fetchAccessByTenant(tenantId: string, opts: { includeArchived?: boolean } = {}): Promise<AccessGroupItem[]> {
  const qs = opts.includeArchived ? "?includeArchived=true" : "";
  const { data } = await authFetch<AccessGroupItem[]>(
    `/access/by-tenant/${encodeURIComponent(tenantId)}${qs}`,
  );
  return data;
}

/** Access groups keyed by tenantId, as returned by GET /access/by-tenant/batch. */
export type AccessGroupsByTenant = Record<string, AccessGroupItem[]>;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Fetch access groups for many tenants at once, collapsing the IAM dashboard's
 * former per-tenant N+1 into chunked requests. Partial chunk failures merge
 * successful chunks; fails only when every chunk rejects.
 */
export async function fetchAccessGroupsByTenants(
  tenantIds: string[],
  opts: { includeArchived?: boolean } = {},
): Promise<AccessGroupsByTenant> {
  if (tenantIds.length === 0) return {};

  const batches = chunk(tenantIds, MAX_ACCESS_BY_TENANT_BATCH);
  const results = await Promise.allSettled(
    batches.map(async (ids) => {
      const params = new URLSearchParams({ tenantIds: ids.join(",") });
      if (opts.includeArchived) params.set("includeArchived", "true");
      const { data } = await authFetch<AccessGroupsByTenant>(
        `/access/by-tenant/batch?${params}`,
      );
      return data;
    }),
  );

  const merged: AccessGroupsByTenant = {};
  let fulfilled = 0;
  let firstRejection: unknown;
  for (const result of results) {
    if (result.status === "fulfilled") {
      fulfilled++;
      Object.assign(merged, result.value);
    } else if (firstRejection === undefined) {
      firstRejection = result.reason;
    }
  }
  if (fulfilled === 0) {
    throw firstRejection ?? new Error("Failed to load access groups batch");
  }
  return merged;
}

export async function fetchAccessGroup(accessId: string): Promise<AccessGroupDetail> {
  const { data } = await authFetch<AccessGroupDetail>(
    `/access/${encodeURIComponent(accessId)}`,
  );
  return data;
}

export type IamAccessSortBy = "name" | "updatedAt" | "memberCount" | "tenantName";

export interface IamAccessListFilters {
  search?: string;
  tenantId?: string;
  type?: "SYSTEM" | "CUSTOM";
  includeArchived?: boolean;
  sortBy?: IamAccessSortBy;
  sortOrder?: "asc" | "desc";
  limit?: number;
}

export interface IamAccessListItem {
  id: string;
  name: string;
  description: string | null;
  type: "SYSTEM" | "CUSTOM";
  tenantId: string;
  tenantName: string;
  tenantType: "API" | "ORGANIZATION" | "SUPER_TENANT";
  tenantLogoUrl: string | null;
  tenantBrandColor?: string | null;
  tenantDeletedAt: string | null;
  updatedAt: string;
  deletedAt: string | null;
  memberCount: number;
  permissionCount: number;
}

export interface PaginatedAccess {
  items: IamAccessListItem[];
  nextCursor?: string | null;
  hasMore: boolean;
  totalCount?: number | null;
}

export async function fetchIamAccessPage(
  filters: IamAccessListFilters,
  cursor?: string,
): Promise<PaginatedAccess> {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  if (cursor) params.set("cursor", cursor);
  if (filters.search) params.set("search", filters.search);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.type) params.set("type", filters.type);
  if (filters.includeArchived) params.set("includeArchived", "true");
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  const { data } = await authFetch<PaginatedAccess>(`/access/all?${params}`);
  return data;
}

// ─── Amber Path Endpoints ───────────────────────────────────────────

export interface AmberResult {
  correlationId: string;
}

async function amberAction(
  url: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body: Record<string, unknown>,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<AmberResult> {
  const { correlationId } = await authFetch<void>(url, {
    method,
    body: JSON.stringify({ ...body, idempotencyKey }),
  });
  if (!correlationId) {
    throw new Error("Amber response missing correlationId");
  }
  return { correlationId };
}

// ─── Poll Status ────────────────────────────────────────────────────

export interface StatusResult {
  status: "ACCEPTED" | "COMPLETED" | "FAILED";
  error?: string;
  message?: string;
  [key: string]: unknown;
}

function abortedPollError(): Error {
  const error = new Error("Aborted");
  error.name = "AbortError";
  return error;
}

function waitForNextPoll(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortedPollError());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortedPollError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function pollStatus(
  basePath: string,
  correlationId: string,
  maxAttempts = 20,
  intervalMs = 500,
  signal?: AbortSignal,
  /**
   * Wall-clock budget in ms when this argument is a number. When set, the
   * attempt cap is not the stop condition: polling continues until a terminal
   * status or this budget. A budget expiry throws `PollBudgetElapsedError`.
   *
   * When this argument is a function, it is invoked on each poll (including
   * non-terminal statuses) for progress UI.
   */
  budgetMsOrOnUpdate?: number | ((status: StatusResult) => void),
  onUpdate?: (status: StatusResult) => void,
): Promise<StatusResult> {
  const budgetMs = typeof budgetMsOrOnUpdate === "number" ? budgetMsOrOnUpdate : undefined;
  const progress =
    typeof budgetMsOrOnUpdate === "function" ? budgetMsOrOnUpdate : onUpdate;
  const startedAt = Date.now();
  const attemptLimit = budgetMs == null ? maxAttempts : Number.POSITIVE_INFINITY;
  for (let i = 0; i < attemptLimit; i++) {
    if (signal?.aborted) throw abortedPollError();
    if (budgetMs != null && Date.now() - startedAt >= budgetMs) {
      throw new PollBudgetElapsedError();
    }
    const { data } = await authFetch<StatusResult>(
      `${basePath}/status/${correlationId}`,
    );
    if (signal?.aborted) throw abortedPollError();
    progress?.(data);
    if (data.status === "COMPLETED" || data.status === "FAILED") {
      return data;
    }
    const delay = intervalMs * Math.min(2 ** i, 8);
    if (budgetMs == null) {
      await waitForNextPoll(delay, signal);
      continue;
    }
    const remaining = budgetMs - (Date.now() - startedAt);
    if (remaining <= 0) throw new PollBudgetElapsedError();
    await waitForNextPoll(Math.min(delay, remaining), signal);
  }
  throw new Error("Polling timed out");
}

// ─── User Mutations ─────────────────────────────────────────────────

export function registerUser(data: { email: string; displayName: string; tenantId: string; groupIds?: string[] }) {
  return amberAction("/users/register", "POST", data);
}

export function updateUser(data: { userId: string; tenantId: string; changes: Record<string, unknown> }) {
  return amberAction("/users/update", "PUT", data);
}

export function deactivateUser(data: { userId: string; tenantId: string; reason?: string }) {
  return amberAction("/users/deactivate", "DELETE", data);
}

export function reactivateUser(data: { userId: string; tenantId: string }) {
  return amberAction("/users/reactivate", "POST", data);
}

export function grantUserAccess(data: { userId: string; accessId: string; tenantId: string }) {
  return amberAction("/users/access/grant", "POST", data);
}

export function revokeUserAccess(data: { userId: string; accessId: string; tenantId: string }) {
  return amberAction("/users/access/revoke", "POST", data);
}

export function bulkGrantUserAccess(data: { userId: string; grants: Array<{ accessId: string; tenantId: string }>; tenantId: string }) {
  return amberAction("/users/access/bulk-grant", "POST", data);
}

export function bulkRevokeUserAccess(data: { userId: string; revocations: Array<{ accessId: string; tenantId: string }>; tenantId: string }) {
  return amberAction("/users/access/bulk-revoke", "POST", data);
}

export function replaceUserAccess(data: { userId: string; oldAccessId: string; newAccessId: string; tenantId: string }) {
  return amberAction("/users/access/replace", "POST", data);
}

// ─── Tenant Mutations ───────────────────────────────────────────────

export function provisionTenant(data: {
  name: string;
  type: string;
  tenantId: string;
  copyAccessFromTenantId: string;
  productLine?: string;
  retoolUserId?: string | null;
  description?: string | null;
  brandColor?: string | null;
  logoUrl?: string | null;
  logoUploadId?: string | null;
  industryIndices?: string[];
  /** Catalogue refs — the field name and shape ProvisionTenantSchema validates. */
  preflightMarketRefs?: AddPreflightMarketInput[];
  /** Client onboarding + customer setup; omitted when New Organization skips onboarding. */
  onboarding?: NewOrganizationOnboardingInput;
  /** Customer partition for a create without onboarding; never sent together with `onboarding`. */
  customerPartition?: string;
  /** Same UUID the onboarding payload and every later retry reuse. */
  idempotencyKey: string;
}) {
  return amberAction("/tenants/provision", "POST", data, data.idempotencyKey);
}

export function updateTenant(data: { tenantId: string; changes: Record<string, unknown> }) {
  return amberAction("/tenants/update", "PUT", data);
}

export async function adminUpdateTenantSettings(tenantId: string, data: {
  defaultIndustry?: string | null;
  defaultBrand?: string | null;
  customerPartition?: string | null;
  conversionsMonthly?: number;
  conversionsWeekly?: number;
  currency?: CurrencyCode;
  defaultNorthStar?: NorthStarKpi | null;
  marketplaceOrdersInboxEmails?: string[];
  marketplaceOrderHandoffDefaults?: MarketplaceOrderHandoffDefaults;
  industryDefaultBrands?: IndustryDefaultBrandUpdate[];
  bqSyncFailureSlackAlertEnabled?: boolean;
  enabledOpportunityTypes?: EnabledOpportunityTypes;
}) {
  await authFetch(`/tenants/${encodeURIComponent(tenantId)}/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export interface AdminTenantSettingsData {
  defaultIndustry: string | null;
  defaultBrand: string | null;
  customerPartition: string | null;
  isBqClientSharingApproved: boolean;
  conversionsMonthly: number;
  conversionsWeekly: number;
  currency: CurrencyCode;
  defaultNorthStar: NorthStarKpi | null;
  marketplaceOrdersInboxEmails?: string[];
  marketplaceOrdersInboxEmail: string | null;
  marketplaceOrderHandoffDefaults?: MarketplaceOrderHandoffDefaults | null;
  products: Array<{ name: string; isFocused: boolean }>;
  productTypes: Array<{ name: string }>;
  /** Only industries with a configured default. Optional while the API rolls out. */
  industryDefaultBrands?: Array<{ industry: string; defaultBrand: string | null }>;
  bqSyncFailureSlackAlertEnabled?: boolean;
  bqCatalogAsOfAt?: string | null;
  enabledOpportunityTypes?: EnabledOpportunityTypes;
}

export interface AdminTenantSyncRunsData {
  runs: import("@nexus/contracts").TenantBqSyncRun[];
  totalCount: number;
}

export async function fetchAdminTenantSettings(tenantId: string): Promise<AdminTenantSettingsData> {
  const { data } = await authFetch<AdminTenantSettingsData>(`/tenants/${encodeURIComponent(tenantId)}/settings`);
  return data;
}

/** Non-global Pre Flight catalogue refs assigned to a tenant (super-tenant IAM). */
export async function fetchAdminTenantPreflightMarketRefs(
  tenantId: string,
): Promise<AddPreflightMarketInput[]> {
  const { data } = await authFetch<AddPreflightMarketInput[]>(
    `/tenants/${encodeURIComponent(tenantId)}/preflight-market-refs`,
  );
  return data;
}

/** Replace a tenant's non-global Pre Flight markets (super-tenant IAM). */
export async function replaceAdminTenantPreflightMarketRefs(
  tenantId: string,
  refs: AddPreflightMarketInput[],
): Promise<AddPreflightMarketInput[]> {
  const { data } = await authFetch<AddPreflightMarketInput[]>(
    `/tenants/${encodeURIComponent(tenantId)}/preflight-market-refs`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refs }),
    },
  );
  return data;
}

export async function fetchAdminTenantSyncRuns(tenantId: string): Promise<AdminTenantSyncRunsData> {
  const { data } = await authFetch<AdminTenantSyncRunsData>(`/tenants/${encodeURIComponent(tenantId)}/sync-runs`);
  return data;
}

export async function triggerTenantSync(tenantId: string): Promise<void> {
  await authFetch(`/tenants/${encodeURIComponent(tenantId)}/sync`, {
    method: 'POST',
  });
}

export function deactivateTenant(data: { tenantId: string }) {
  return amberAction("/tenants/deactivate", "DELETE", data);
}

export function reactivateTenant(data: { tenantId: string }) {
  return amberAction("/tenants/reactivate", "POST", data);
}

// ─── Tenant Onboarding (client context) ─────────────────────────────
// Amber jobs on the tenants routes; status lives under /users/status.

export const TENANT_ONBOARDING_POLL_BASE_PATH = "/users";

/** A worker FAILED result, keeping the `ClientContextErrorCodes` value and any rejected field for the UI. */
export class TenantOnboardingJobError extends Error {
  override readonly name = "TenantOnboardingJobError";
  constructor(
    public readonly code: string | undefined,
    message: string,
    public readonly field?: string,
  ) {
    super(message);
  }
}

function toTenantOnboardingJobError(status: StatusResult, fallbackMessage: string): TenantOnboardingJobError {
  const field = typeof status.field === "string" ? status.field : undefined;
  return new TenantOnboardingJobError(status.error, status.message ?? fallbackMessage, field);
}

/** Whether this deployment accepts onboarding writes. The server enforces it; the UI only hides controls. */
export async function fetchOnboardingAvailability(): Promise<OnboardingWritesAvailability> {
  const { data } = await authFetch<unknown>("/tenants/onboarding/availability");
  return OnboardingWritesAvailabilitySchema.parse(data);
}

function tenantOnboardingPath(tenantId: string) {
  return `/tenants/${encodeURIComponent(tenantId)}/onboarding`;
}

export async function fetchTenantOnboarding(tenantId: string): Promise<TenantOnboardingReadModel> {
  const { correlationId } = await amberAction(`${tenantOnboardingPath(tenantId)}/fetch`, "POST", {});
  const status = await pollStatus(TENANT_ONBOARDING_POLL_BASE_PATH, correlationId);
  if (status.status === "FAILED") {
    throw toTenantOnboardingJobError(status, "Could not load onboarding");
  }
  return TenantOnboardingReadModelSchema.parse(status.result);
}

/** How long the review waits for the BigQuery check before treating the customer as unverified. */
const CUSTOMER_PARTITION_CHECK_BUDGET_MS = 15_000;

export interface CustomerPartitionCheckStart {
  /** Nexus organizations already using the partition; answered immediately. */
  usage: CustomerPartitionUsage;
  /** Poll this for the BigQuery check; absent when none was started. */
  correlationId: string | undefined;
}

/** New Organization review, step 1: Nexus usage now, and the read-only BigQuery check started. Writes nothing. */
export async function startCustomerPartitionCheck(customerPartition: string): Promise<CustomerPartitionCheckStart> {
  const { data, correlationId } = await authFetch<unknown>("/tenants/customer-partition/check", {
    method: "POST",
    body: JSON.stringify({ customerPartition }),
  });
  return { usage: CustomerPartitionUsageSchema.parse(data), correlationId };
}

/**
 * New Organization review, step 2: what the data-sync check found for the exact
 * customer name. Null means it could not be verified (the job failed, timed
 * out, or answered with something unreadable) — never "customer does not exist".
 */
export async function awaitCustomerPartitionCheck(correlationId: string): Promise<CustomerPartitionCheckResult | null> {
  try {
    const status = await pollStatus(TENANT_ONBOARDING_POLL_BASE_PATH, correlationId, 0, 500, undefined, CUSTOMER_PARTITION_CHECK_BUDGET_MS);
    if (status.status !== "COMPLETED") return null;
    const parsed = CustomerPartitionCheckResultSchema.safeParse(status.result);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** New Organization resubmit (CREATE_TENANT) after the onboarding create failed; setup is mandatory. */
export function createTenantOnboarding(data: {
  tenantId: string;
  fields: Record<string, unknown>;
  setup: ClientsParamsSetupInput;
  compKey: string;
  /** Reused across the first publish and every retry of this create. */
  idempotencyKey: string;
}) {
  return amberAction(
    tenantOnboardingPath(data.tenantId),
    "POST",
    { fields: data.fields, setup: data.setup, compKey: data.compKey },
    data.idempotencyKey,
  );
}

/** Existing organization with no onboarding record (IDENTITY_HUB_UPDATE_TENANT); `setup` only when Nexus has no setup snapshot. */
export function initializeTenantOnboarding(data: {
  tenantId: string;
  fields: Record<string, unknown>;
  setup?: ClientsParamsSetupInput;
  compKey: string;
  /** Reused across the first publish and every retry of this initialize. */
  idempotencyKey: string;
}) {
  return amberAction(
    `${tenantOnboardingPath(data.tenantId)}/initialize`,
    "POST",
    {
      fields: data.fields,
      compKey: data.compKey,
      ...(data.setup ? { setup: data.setup } : {}),
    },
    data.idempotencyKey,
  );
}

/** Waits for a New Organization onboarding create — the one provisioning dispatched, or a resubmit. */
export async function awaitNewOrgOnboarding(
  correlationId: string,
  onUpdate?: (status: StatusResult) => void,
): Promise<StatusResult> {
  const status = await pollStatus(TENANT_ONBOARDING_POLL_BASE_PATH, correlationId, 20, 500, undefined, onUpdate);
  if (status.status === "FAILED") {
    throw toTenantOnboardingJobError(status, "Client onboarding was not saved");
  }
  return status;
}

export function editTenantOnboarding(data: { tenantId: string; changes: Record<string, unknown>; compKey?: string }) {
  return amberAction(tenantOnboardingPath(data.tenantId), "PATCH", {
    changes: data.changes,
    ...(data.compKey ? { compKey: data.compKey } : {}),
  });
}

// ─── Access Group Mutations ─────────────────────────────────────────

export function createAccessGroup(data: { name: string; tenantId: string; description?: string; permissions?: Array<{ type: string; method: string; route: string }> }) {
  return amberAction("/access/create", "POST", data);
}

export function createAccessGroupsBulk(data: {
  name: string;
  tenantIds: string[];
  description?: string;
  permissions?: Array<{ type: string; method: string; route: string }>;
}) {
  return amberAction("/access/create/bulk", "POST", {
    ...data,
    tenantId: data.tenantIds[0]!,
  });
}

export function updateAccessGroup(data: { accessId: string; tenantId: string; changes: { name?: string; description?: string } }) {
  return amberAction("/access/update", "PUT", data);
}

export function deleteAccessGroup(data: { accessId: string; tenantId: string }) {
  return amberAction("/access/delete", "DELETE", data);
}

export function restoreAccessGroup(data: { accessId: string; tenantId: string }) {
  return amberAction("/access/restore", "POST", data);
}

export function updateAccessPermissions(data: { accessId: string; tenantId: string; permissions: Array<{ type: string; method: string; route: string }> }) {
  return amberAction("/access/permissions", "PUT", data);
}

export type TemplateStatus = "ACTIVE" | "ARCHIVED";

export interface AccessTemplateDto {
  id: string;
  name: string;
  description: string | null;
  scopeMode: TemplateScopeMode;
  status: TemplateStatus;
  createdByUserId: string | null;
  canBeEditedByTenantId: string | null;
  createdAt: string;
  updatedAt: string;
  tenantIds: string[];
  tenants: Array<{ id: string; name: string; type: string; logoUrl?: string | null }>;
  ownerTenant: { id: string; name: string; type: string; logoUrl?: string | null } | null;
  permissions: Array<{ type: string; method: string; route: string }>;
}

export interface AccessTemplateListFilters {
  search?: string;
  includeArchived?: boolean;
}

export async function fetchAccessTemplates(
  filters: AccessTemplateListFilters = {},
): Promise<AccessTemplateDto[]> {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.includeArchived) params.set("includeArchived", "true");
  const qs = params.toString();
  const { data } = await authFetch<AccessTemplateDto[]>(`/templates${qs ? `?${qs}` : ""}`);
  return data;
}

export async function fetchAccessTemplate(templateId: string): Promise<AccessTemplateDto> {
  const { data } = await authFetch<AccessTemplateDto>(`/templates/${templateId}`);
  return data;
}

export function createAccessTemplate(data: {
  name: string;
  description?: string;
  scopeMode: TemplateScopeMode;
  tenantIds: string[];
  permissions: Array<{ type: string; method: string; route: string }>;
  tenantId: string;
  canBeEditedByTenantId: string;
}) {
  return amberAction("/templates/create", "POST", data);
}

export function updateAccessTemplate(data: {
  templateId: string;
  tenantId: string;
  changes: {
    name?: string;
    description?: string | null;
    tenantIds?: string[];
    permissions?: Array<{ type: string; method: string; route: string }>;
  };
}) {
  return amberAction("/templates/update", "PUT", data);
}

export function changeAccessTemplateStatus(data: {
  templateId: string;
  tenantId: string;
  status: TemplateStatus;
}) {
  return amberAction("/templates/status", "POST", data);
}

export function grantAccessTemplate(data: {
  templateId: string;
  tenantId: string;
  userIds: string[];
  tenantIds?: string[];
}) {
  return amberAction("/templates/grant", "POST", data);
}

export function revokeAccessTemplate(data: {
  templateId: string;
  tenantId: string;
  userIds: string[];
}) {
  return amberAction("/templates/revoke", "POST", data);
}

export function applyTemplateToOrg(data: { templateId: string; tenantId: string }) {
  return amberAction("/templates/apply-to-org", "POST", data);
}

export function removeTemplateFromOrg(data: { templateId: string; tenantId: string }) {
  return amberAction("/templates/remove-from-org", "POST", data);
}

export interface CascadePermissionDiff {
  added: Array<{ type: string; method: string; route: string }>;
  removed: Array<{ type: string; method: string; route: string }>;
}

export interface CascadeTenantPreview {
  tenantId: string;
  accessId?: string;
  status: "ok" | "failed" | "would-create" | "would-remove" | "unchanged";
  diff: CascadePermissionDiff;
  holderExpansionCount: number;
}

export interface CascadePreview {
  templateId?: string;
  name: string;
  tenants: CascadeTenantPreview[];
  addedTenantCount: number;
  removedTenantCount: number;
  updatedGroupCount: number;
  holderExpansionCount: number;
}

export async function previewAccessTemplateCascade(data: {
  templateId?: string;
  tenantId: string;
  name: string;
  scopeMode?: TemplateScopeMode;
  tenantIds: string[];
  permissions: Array<{ type: string; method: string; route: string }>;
}): Promise<CascadePreview> {
  const { data: preview } = await authFetch<CascadePreview>("/templates/cascade-preview", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return preview;
}

// ─── Invite ─────────────────────────────────────────────────────────

// ─── IAM Stats ──────────────────────────────────────────────────────

export type IamWindow = '1d' | '7d' | '30d';

export interface IamStatMetric {
  current: number;
  delta: number;
}

export interface IamStats {
  people: IamStatMetric;
  orgs: IamStatMetric & { totalInactive: number };
  privileged: IamStatMetric;
  pending: IamStatMetric & { oldestDays: number | null };
}

export async function fetchIamStats(window: IamWindow): Promise<IamStats> {
  const { data } = await authFetch<IamStats>(`/users/stats?window=${window}`);
  return data;
}

// ─── Invite ──────────────────────────────────────────────────────────

/**
 * Result from POST /invites.
 * - New invite: id, token, email, expiresAt
 * - Existing pending invite resent: id, email, expiresAt (no token)
 * - Existing ACTIVE user notified instead of invited: email only
 */
export interface CreateInviteResult {
  email: string;
  id?: string;
  token?: string;
  expiresAt?: string;
}

export interface InviteTemplateGrant {
  templateId: string;
  tenantId: string;
}

export async function createInvite(data: {
  email: string;
  accessIds?: string[];
  templateGrants?: InviteTemplateGrant[];
  ttlHours?: number;
  message?: string;
  sendEmail?: boolean;
}): Promise<CreateInviteResult> {
  const { data: result } = await authFetch<CreateInviteResult>(
    "/invites",
    { method: "POST", body: JSON.stringify(data) },
  );
  return result;
}

/** Org that was successfully granted on an invite. */
export interface BulkInviteGrantedOrg {
  tenantId: string;
  orgName: string | null;
  accessId: string;
  roleName: string | null;
}

/** Org that was skipped for a specific person (with reason). */
export interface BulkInviteSkippedOrg {
  tenantId?: string | null;
  orgName?: string | null;
  reason: string;
}

/**
 * Max emails accepted in one bulk-invite request. Mirrors the backend Zod cap
 * (MAX_INVITE_EMAILS in services/api .../invite.schemas.ts) — keep in sync.
 */
export const MAX_INVITE_EMAILS = 50;

/** Error code the backend returns when the bulk-invite email cap is exceeded. */
export const INVITE_EMAILS_LIMIT_CODE = "INVITE_EMAILS_LIMIT";

/**
 * Per-person result from POST /invites/bulk.
 * Each person receives ONE invite token granting access to all `orgs`
 * that weren't skipped. `skipped` lists the orgs that were excluded and why.
 */
export interface BulkInviteResultItem {
  email: string;
  success: boolean;
  /** Present when success is true. */
  id?: string;
  token?: string;
  expiresAt?: string;
  /** Organizations successfully granted on this invite. */
  orgs?: BulkInviteGrantedOrg[];
  /** Organizations skipped with their reasons. */
  skipped?: BulkInviteSkippedOrg[];
  /** Top-level error when the entire person's invite failed (e.g. user already active). */
  error?: string;
}

/**
 * Create invites for one or more people across one or more organizations.
 * Each person receives ONE invite token/email granting access to all the
 * organizations they qualify for. Orgs that fail per-person checks are
 * reported in `skipped` without blocking the other orgs.
 */
export async function createInvitesBulk(data: {
  emails: string[];
  accessIds?: string[];
  templateGrants?: InviteTemplateGrant[];
  ttlHours?: number;
  message?: string;
  sendEmail?: boolean;
}): Promise<BulkInviteResultItem[]> {
  const { data: results } = await authFetch<BulkInviteResultItem[]>(
    "/invites/bulk",
    { method: "POST", body: JSON.stringify(data) },
  );
  return results;
}

export async function regenerateInvite(data: { email: string; ttlHours?: number }): Promise<{ id: string; token: string; email: string; expiresAt: string }> {
  const { data: result } = await authFetch<{ id: string; token: string; email: string; expiresAt: string }>(
    "/invites/regenerate",
    { method: "POST", body: JSON.stringify(data) },
  );
  return result;
}

/**
 * Fetch an existing invite's token so its link can be copied WITHOUT rotating
 * it — unlike regenerate, this leaves the invitee's already-emailed link valid
 * (AD-1714). Read-only; the invite must still be pending (not used/expired).
 */
export async function fetchInviteLink(id: string): Promise<{ id: string; token: string; email: string; expiresAt: string }> {
  const { data: result } = await authFetch<{ id: string; token: string; email: string; expiresAt: string }>(
    `/invites/${encodeURIComponent(id)}/link`,
  );
  return result;
}

export type InviteStatus = "pending" | "expired" | "accepted";

export interface InviteAccessItem {
  accessId: string;
  accessName: string | null;
  tenantId: string;
  tenantName: string | null;
  roleName: string | null;
}

export interface InviteListItem {
  id: string;
  email: string;
  token: string | null;
  message: string | null;
  sendEmail: boolean;
  /** Legacy single-access fields — present for back-compat; prefer `accesses`. */
  accessId: string | null;
  accessName: string | null;
  tenantId: string | null;
  tenantName: string | null;
  /** Multi-org access grants on this invite. */
  accesses: InviteAccessItem[];
  createdBy: string | null;
  createdByName: string | null;
  createdByEmail: string | null;
  createdByPhotoUrl: string | null;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  status: InviteStatus;
}

export interface InviteListPage {
  items: InviteListItem[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount?: number | null;
  statusCounts?: Partial<Record<InviteStatus | "all", number>> | null;
}

export interface InviteListFilters {
  status?: InviteStatus;
  tenantId?: string;
  accessId?: string;
  email?: string;
  search?: string;
  cursor?: string;
  limit?: number;
  sortBy?: "createdAt" | "email" | "expiresAt";
  sortOrder?: "asc" | "desc";
}

export async function fetchAllInvites(filters: InviteListFilters = {}): Promise<InviteListPage> {
  const params = new URLSearchParams();
  if (filters.status)   params.set("status", filters.status);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.accessId) params.set("accessId", filters.accessId);
  if (filters.email)    params.set("email", filters.email);
  if (filters.search)   params.set("search", filters.search);
  if (filters.cursor)   params.set("cursor", filters.cursor);
  if (filters.limit)    params.set("limit", String(filters.limit));
  if (filters.sortBy)   params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  const qs = params.toString();
  const { data } = await authFetch<InviteListPage>(`/invites${qs ? `?${qs}` : ""}`);
  return data;
}

export async function resendInvite(id: string, ttlHours?: number): Promise<{ id: string; token: string; email: string; expiresAt: string }> {
  const { data } = await authFetch<{ id: string; token: string; email: string; expiresAt: string }>(
    `/invites/${encodeURIComponent(id)}/resend`,
    { method: "POST", body: JSON.stringify({ ttlHours }) },
  );
  return data;
}

export async function revokeInvite(id: string): Promise<void> {
  await authFetch<void>(`/invites/${encodeURIComponent(id)}`, { method: "DELETE" });
}
